import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { sec } from "../../../security";
import { addNotification } from "../../notifications/notificationsSlice";
import { CandidateNote, CandidateNotesResponse, CandidateNotesView } from "./model/candidateNote";

const apiServerUrl = process.env.REACT_APP_HAISTACK_API_URL;

export const candidateNotesApi = createApi({
  reducerPath: "candidateNotesApi",
  tagTypes: ["CandidateNotes", "CandidateNote"],
  baseQuery: fetchBaseQuery({
    baseUrl: apiServerUrl,
    prepareHeaders: async (headers) => {
      const token = await sec.getAccessTokenSilently()();
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    }
  }),
  endpoints: (builder) => ({
    getCandidateNotes: builder.query<CandidateNotesView, string>({
      query: (qs) => `/api/v2/candidate_notes?${qs}`,
      transformResponse: (response: CandidateNotesResponse): CandidateNotesView => ({
        data: response.candidate_notes,
        count: response.meta?.count ?? 0
      }),
      providesTags: ["CandidateNotes"]
    }),
    getCandidateNote: builder.query<CandidateNote, string>({
      query: (id) => `/api/v2/candidate_notes/${id}`,
      transformResponse: (response: { candidate_note: CandidateNote }) => response.candidate_note,
      providesTags: (result, error, id) => [{ type: "CandidateNote", id }]
    }),
    createCandidateNote: builder.mutation<CandidateNote, { candidate_note: Partial<CandidateNote> }>({
      query: (body) => ({
        url: "/api/v2/candidate_notes",
        method: "POST",
        body
      }),
      invalidatesTags: ["CandidateNotes"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Candidate note created successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to create candidate note",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    updateCandidateNote: builder.mutation<CandidateNote, { id: string; candidate_note: Partial<CandidateNote> }>({
      query: ({ id, candidate_note }) => ({
        url: `/api/v2/candidate_notes/${id}`,
        method: "PATCH",
        body: { candidate_note }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "CandidateNote", id },
        "CandidateNotes"
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Candidate note updated successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to update candidate note",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    deleteCandidateNote: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/v2/candidate_notes/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["CandidateNotes"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Candidate note deleted successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to delete candidate note",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    })
  })
});

export const {
  useGetCandidateNotesQuery,
  useGetCandidateNoteQuery,
  useCreateCandidateNoteMutation,
  useUpdateCandidateNoteMutation,
  useDeleteCandidateNoteMutation
} = candidateNotesApi;
